import type { FC } from 'react'
import type { MenuItem } from '@/types'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatPrice } from '@/lib/format'
import { revealDelay } from '@/lib/motion'
import { Photo } from '@/components/generals/photo'
import { SectionHeading } from '@/components/generals/section-heading'

import { SHELL } from '@/constants/site'

interface SignatureGridProps {
    items: Array<MenuItem>
    onSelect: (item: MenuItem) => void
}

export const SignatureGrid: FC<SignatureGridProps> = ({ items, onSelect }) => (
    <section id='signature' aria-labelledby='signature-title' className='border-t border-line py-20 lg:pt-32 lg:pb-40'>
        <div className={SHELL}>
            <div className='flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'>
                <SectionHeading
                    id='signature-title'
                    label='ویژه‌های ما'
                    title={<>همان‌هایی که <span className='italic'>ما را با آن‌ها می‌شناسند</span></>}
                    intro='اولین‌هایی که هر روز تمام می‌شوند؛ هر صبح با همان حوصله‌ی همیشگی پخته می‌شوند.'
                />

                <Link href='/menu' className='link-underline inline-flex items-center gap-2 self-start text-sm font-medium lg:self-auto' data-reveal='up'>
                    دیدن منوی کامل
                    <ArrowRight className='size-4 rtl:-scale-x-100' aria-hidden='true' />
                </Link>
            </div>

            <ul className='mt-14 grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:mt-20 lg:grid-cols-3 lg:gap-x-10 lg:gap-y-24'>
                {items.map((item, index) => (
                    <li key={item.id} className={cn('group relative', index % 3 === 1 && 'lg:translate-y-24')}>
                        <Photo
                            src={item.image}
                            alt={item.imageAlt}
                            sizes='(min-width: 1440px) 440px, (min-width: 1024px) 30vw, (min-width: 640px) 46vw, 100vw'
                            className='aspect-[4/5]'
                            imageClassName='transition-transform duration-[1400ms] ease-(--ease-soft) group-hover:scale-[1.035]'
                            style={revealDelay((index % 3) * 120)}
                            fallback={item.name}
                        />

                        <div className='mt-5' data-reveal='up' style={revealDelay((index % 3) * 120 + 120)}>
                            <p className='label-caps text-crust-soft' {...(item.categoryId ? { 'data-mdlfy-cms': `menu_categories:${item.categoryId}:name` } : {})}>{item.categoryName}</p>

                            <div className='mt-2 flex items-baseline justify-between gap-4'>
                                <h3 className='font-heading text-[1.85rem] leading-[1.3] font-normal'>
                                    <button
                                        type='button'
                                        onClick={() => onSelect(item)}
                                        className='cursor-pointer text-start after:absolute after:inset-0 focus-visible:outline-none focus-visible:after:outline-2 focus-visible:after:outline-offset-4 focus-visible:after:outline-amber'
                                    >
                                        <span data-mdlfy-cms={`menu_items:${item.id}:name`}>{item.name}</span>
                                    </button>
                                </h3>

                                <span className='shrink-0 tabular-nums'>{formatPrice(item.price)}</span>
                            </div>

                            <p className='mt-3 max-w-sm text-[0.9375rem] leading-relaxed text-crust-soft' data-mdlfy-cms={`menu_items:${item.id}:description`}>{item.description}</p>

                            <span className='mt-4 inline-flex items-center gap-1.5 text-sm font-medium' aria-hidden='true'>
                                <span className='link-underline group-hover:[background-size:100%_1px]'>دیدن جزئیات</span>
                                <ArrowRight className='size-3.5 transition-transform duration-500 ease-(--ease-soft) rtl:-scale-x-100 group-hover:translate-x-1 rtl:group-hover:-translate-x-1' />
                            </span>
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    </section>
)
